import React, { useEffect } from 'react';
import { Checkbox, ConfigProvider } from 'antd';
import styles from './multi_select.module.scss';
import type { GetProp } from 'antd';
import styled from 'styled-components';
import './checked_styles.css';
import { useAtom } from 'jotai';
import {
  multiSelectResponseAtom,
  multiSelectValuesAtom,
  careProviderConfiguration,
} from '../../../store';
import { InteractionUserResponses } from 'models/interactions.interface';

interface MultiSelectProps {
  answers: { label: string; value: number }[];
  questionId: number;
  isBeingEdited: boolean;
  disabled: boolean;
  rawAnswers: RawAnswer[];
  selectedAnswers?: InteractionUserResponses['answers'];
}

interface RawAnswer {
  text: string;
  id: number;
  response?: string;
}

interface Props {
  color: string;
}

const CheckboxGroup = styled(Checkbox.Group)<Props>`
  .ant-checkbox-wrapper-checked {
    background: ${props => props.color};
    color: white;
  }
  .ant-checkbox-checked .ant-checkbox-inner:after {
    background-color: white;
    border-color: ${props => props.color};
  }
`;

const MultiSelect: React.FC<MultiSelectProps> = ({
  answers,
  questionId,
  isBeingEdited,
  disabled,
  rawAnswers,
  selectedAnswers,
}) => {
  const [multiSelectValues, setMultiSelectValues] = useAtom(
    multiSelectValuesAtom,
  );
  const [careProviderStyles] = useAtom(careProviderConfiguration);
  const [, setMultiSelectResponse] = useAtom(multiSelectResponseAtom);

  useEffect(() => {
    getPreviousResponse();
  }, []);

  const onChange: GetProp<typeof Checkbox.Group, 'onChange'> = (
    checkedValues,
  ) => {
    setMultiSelectValues(checkedValues);
    const selected = answers.filter(answer =>
      checkedValues.some(checkedValue => answer.value === checkedValue),
    );

    const stored = selected.map((answer) => {
      const answerResponse = rawAnswers.find(
        ans => ans.id === answer.value,
      )?.response;
      const userResp = { id: Number(answer.value), text: answer.label };
      return answerResponse
        ? { ...userResp, response: answerResponse }
        : userResp;
    });
    setMultiSelectResponse({ questionId: questionId, answers: stored });
  };
  const getPreviousResponse = () => {
    if (!isBeingEdited || !selectedAnswers?.length) return [];
    const previousResponse = selectedAnswers || [];
    if (previousResponse.length) {
      const savedAnswers = previousResponse.map(answer => answer?.id || '');
      setMultiSelectValues(savedAnswers);
    }
    else return [];
  };
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: careProviderStyles.colorPrimary,
        },
      }}
    >
      <CheckboxGroup
        color={careProviderStyles.colorPrimary}
        className={`${styles.checkboxGroup} checked-style`}
        value={multiSelectValues}
        onChange={onChange}
        disabled={disabled}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
        data-cy="checkbox_group"
      >
        {answers.map((answer, index) => {
          return (
            <Checkbox
              key={answer.value}
              value={answer.value}
              data-cy={`checkbox_${index}`}
              data-cy-answer-label={answer.label}
              className={styles.checkbox}
            >
              {answer.label}
            </Checkbox>
          );
        })}
      </CheckboxGroup>
    </ConfigProvider>
  );
};

export default MultiSelect;
